import { Box, Stack, Typography } from "@mui/material";
import { type MouseEvent } from "react";

import type {
  InventorySummary,
  ProductCard,
  ProductCardPrice,
  ProductCardVariantAttribute,
} from "@/types/ingestedProduct";

import { SKUStoreResourceCardDetailRow } from "@/features/SKUStore/components/ResourceCard/SKUStoreResourceCard";
import {
  BadgeActionsRow,
  ImageTileShell,
  TilePrimaryActionButton,
} from "@/features/SKUStore/components/ResourceCard/SKUStoreResourceCard.shared";
import { productStatusBadgeItems } from "@/features/SKUStore/components/Status/statusBadgeItems";

/**
 * Lifts footer controls above the title's stretched ::after hit area, so Edit
 * gets its own clicks and the badge tooltips get hover.
 */
const RAISED_CONTROL_SX = {
  position: "relative",
  zIndex: 1,
  display: "flex",
} as const;

function formatCardPrice(price: ProductCardPrice | undefined): string | null {
  const amount = price?.amount;
  if (amount == null || String(amount).trim() === "") {
    return null;
  }
  const n = Number(amount);
  if (Number.isNaN(n)) {
    return null;
  }
  const currency = price?.currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `${currency} ${amount}`;
  }
}

/** e.g. `{ name: "Color", count: 8 }` → "8 Colors" (singular when count is 1). */
function attributeLabel(attribute: ProductCardVariantAttribute): string {
  const suffix = attribute.count === 1 ? "" : "s";
  return `${attribute.count} ${attribute.name}${suffix}`;
}

/** "In stock: 143" plus "(+1 unknown)" when some variants report no quantity. */
export function inventoryDetailLabel(inventory: InventorySummary): string {
  const base = `In stock: ${String(inventory.known_quantity_total)}`;
  return inventory.unknown_quantity_variants > 0
    ? `${base} (+${String(inventory.unknown_quantity_variants)} unknown)`
    : base;
}

export type SKUStoreProductCardProps = {
  card: ProductCard;
  /**
   * Opens the product. Also the whole-card click target, so the card is
   * inert when omitted.
   */
  onEdit?: () => void;
  /** Opens the product's Variants tab from the variant-count chip. */
  onOpenVariants?: () => void;
};

/**
 * Product grid card for the SKU Store "All Products" view. The whole card
 * opens the product through the title button, stretched over the card with
 * ::after, so no interactive control is nested in another (axe
 * `nested-interactive`); the variant chip deep-links to the Variants tab.
 */
export function SKUStoreProductCard({
  card,
  onEdit,
  onOpenVariants,
}: SKUStoreProductCardProps) {
  const priceLabel = formatCardPrice(card.price);
  const attributes = card.variant_counts?.attributes ?? [];
  const variantCount = card.variant_counts?.variants;
  const variantCountLabel =
    variantCount != null
      ? `${String(variantCount)} ${variantCount === 1 ? "variant" : "variants"}`
      : null;
  const inventory = card.inventory ?? null;

  const title = onEdit ? (
    // ::after resolves against the card root, so this button must stay
    // unpositioned: no MUI ButtonBase, which sets position: relative.
    <Box
      component="button"
      type="button"
      aria-label={`Open ${card.title}`}
      onClick={onEdit}
      sx={(theme) => ({
        appearance: "none",
        background: "none",
        border: 0,
        p: 0,
        m: 0,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        minWidth: 0,
        flex: 1,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          // px string, not a number: sx multiplies a bare number by
          // theme.shape.borderRadius.
          borderRadius: `${theme.shape.borderRadiusLg}px`,
        },
        // Ring the whole card, which is what the hit area covers.
        "&:focus-visible": { outline: "none" },
        "&:focus-visible::after": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      })}
    >
      {/* span, not the default <p>: a button only allows phrasing content. */}
      <Typography
        component="span"
        variant="body1"
        noWrap
        sx={{ display: "block" }}
      >
        {card.title}
      </Typography>
    </Box>
  ) : (
    <Typography variant="body1" noWrap sx={{ minWidth: 0, flex: 1 }}>
      {card.title}
    </Typography>
  );

  const titleBlock = (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between" }}
      >
        {title}
        {priceLabel != null && (
          <Typography variant="body1" sx={{ flexShrink: 0 }}>
            {priceLabel}
          </Typography>
        )}
      </Stack>

      {attributes.length > 0 && (
        <Stack spacing={1}>
          {attributes.map((attribute) => (
            <Typography
              key={attribute.name}
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              {attributeLabel(attribute)}
            </Typography>
          ))}
        </Stack>
      )}

      {card.product_id ? (
        <SKUStoreResourceCardDetailRow>
          {card.product_id}
        </SKUStoreResourceCardDetailRow>
      ) : null}

      {inventory ? (
        <SKUStoreResourceCardDetailRow>
          {inventoryDetailLabel(inventory)}
        </SKUStoreResourceCardDetailRow>
      ) : null}

      {/* Unscored products show nothing rather than a placeholder. */}
      {card.sku_score_total != null ? (
        <SKUStoreResourceCardDetailRow>
          SKU Score: {String(Math.round(card.sku_score_total))}
        </SKUStoreResourceCardDetailRow>
      ) : null}
    </Stack>
  );

  // Raised out of the title's hit area, the badge row forwards clicks so a
  // badge still opens the product; the Variants chip stops propagation.
  const footerRow = (
    <BadgeActionsRow
      badges={[
        ...(variantCountLabel != null
          ? [
              {
                key: "variants",
                label: variantCountLabel,
                tone: "success" as const,
                onClick: onOpenVariants,
                // It opens the variant list, so it never folds away.
                pinned: true,
                priority: 0,
              },
            ]
          : []),
        ...productStatusBadgeItems({ flags: card.flags, inventory }),
      ]}
      onBadgesClick={
        onEdit
          ? (event: MouseEvent<HTMLDivElement>) => {
              // Tooltips are portaled to <body> but React still bubbles their
              // events here; only forward clicks inside the row itself.
              if (!event.currentTarget.contains(event.target as Node)) {
                return;
              }
              onEdit();
            }
          : undefined
      }
      badgesSx={
        onEdit ? { ...RAISED_CONTROL_SX, cursor: "pointer" } : undefined
      }
      action={
        <TilePrimaryActionButton action={{ label: "Edit", onClick: onEdit }} />
      }
      actionSx={RAISED_CONTROL_SX}
    />
  );

  return (
    // Positioned so the title's ::after hit area covers the whole card. No
    // role, handlers, or tabIndex here: that would nest the buttons inside it.
    <Box sx={{ height: 1, position: "relative" }}>
      <ImageTileShell
        imageAlt={card.title}
        imageSrc={card.image_url}
        titleBlock={titleBlock}
        statusSlot={footerRow}
      />
    </Box>
  );
}
